"use client";

import React, { useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader, ArrowRight } from "lucide-react";
const PROJECT_TYPES = ["Loft Conversion", "Home Extension", "Double Storey Extension", "Single Storey Extension", "Kitchen Renovation", "Bathroom Renovation", "Other"];
const BUDGET_RANGES = ["Under £20,000", "£20,000 - £50,000", "£50,000 - £100,000", "Over £100,000"];
const inputBaseClasses = "flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-black placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black dark:border-gray-300 dark:focus-visible:ring-white";
function TransformationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage, {
        description: "We will be in touch shortly to discuss your project."
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-6 rounded-xl bg-card p-8 text-center border border-border shadow-lg">
        <div className="flex size-16 items-center justify-center rounded-full bg-green-500/20 text-green-500">
          <svg className="size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Request Received</h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border text-foreground hover:bg-accent">
          Submit Another Project
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Please enter your full name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Your Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="John Doe" className={`${inputBaseClasses} h-10`} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="john@example.com" className={`${inputBaseClasses} h-10`} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+44 7123 456789" className={`${inputBaseClasses} h-10`} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" rules={{
        required: "Please select a project type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Project Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={`${inputBaseClasses} h-10 data-[placeholder]:text-gray-500`}>
                    <SelectValue placeholder="Select a project type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white text-black dark:bg-white dark:text-black border-gray-200">
                  {PROJECT_TYPES.map((type, i) => <SelectItem key={type} value={type} data-index={i} className="focus:bg-gray-100 focus:text-black dark:focus:bg-gray-100 dark:focus:text-black cursor-pointer">
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Estimated Budget (Optional)</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={`${inputBaseClasses} h-10 data-[placeholder]:text-gray-500`}>
                  <SelectValue placeholder="Select a budget range" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-white text-black dark:bg-white dark:text-black border-gray-200">
                {BUDGET_RANGES.map((range, i) => <SelectItem key={range} value={range} data-index={i} className="focus:bg-gray-100 focus:text-black dark:focus:bg-gray-100 dark:focus:text-black cursor-pointer">
                    {range}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Project Details</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Tell us about your vision, specific requirements, and any challenges." className={`${inputBaseClasses} min-h-[120px] resize-y`} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/15 p-4 border border-destructive/30">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-white text-black hover:bg-gray-200 dark:bg-white dark:text-black dark:hover:bg-gray-200 transition-colors">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Submitting Request...
          </> : <>
            Start Your Project
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function BeginYourTransformation() {
  return <section id="begin-your-transformation" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
          
          {}
          <div className="flex flex-col justify-center space-y-8 lg:sticky lg:top-32">
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-border bg-card/50 px-3 py-1 text-sm font-medium text-muted-foreground backdrop-blur-sm">
                <span className="flex size-2 rounded-full bg-white mr-2 animate-pulse"></span>
                Accepting New Projects
              </div>
              <h2 className="text-5xl font-extrabold tracking-tighter sm:text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">Begin <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500" data-ast-id="f:/src/pages/NorthWestLondonExpertBuilder/sections/BeginYourTransformation.tsx|sig:9b2m5z" data-ast-start="8531" data-ast-end="8666">Your</span> <br />project</h2>
            </div>
            
            <p className="max-w-[600px] text-lg text-muted-foreground md:text-xl leading-relaxed">
              Partner with SolidTrust Builders to bring your architectural vision to life in North West London. From bespoke loft conversions to expansive home extensions, our expert team delivers uncompromising quality.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 pt-4">
              <div className="flex flex-col space-y-1">
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Call Us Directly</span>
                <Link to="tel:+442079460123" className="text-xl font-semibold hover:text-gray-300 transition-colors">+44 7810 664982</Link>
              </div>
              <div className="hidden sm:block w-px bg-border"></div>
              <div className="flex flex-col space-y-1">
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Email Us</span>
                <Link to="mailto:info@solidtrustbuilders.co.uk" className="text-xl font-semibold hover:text-gray-300 transition-colors">info@solidtrustbuilders.co.uk</Link>
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-b from-white/10 to-transparent opacity-50 blur-xl"></div>
            
            <div className="relative rounded-2xl border border-border bg-card p-6 sm:p-8 md:p-10 shadow-2xl">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">Project Inquiry</h3>
                <p className="text-muted-foreground">Fill out the details below and our lead architect will contact you within 24 hours.</p>
              </div>

              <FormProvider formId="transformation_form_north_west_london_expert_builder" sectionName="Begin Your Transformation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              project_type: "",
              project_details: "",
              budget: ""
            }} submitText="Start Your Project" successMessage="Thank you for reaching out. Your project inquiry has been successfully submitted.">
                <TransformationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" />
    </section>;
}